import type { HandbookChapter } from '../model';

/**
 * Die Einstellungen: was ein Administrator einrichtet, bevor gearbeitet wird.
 *
 * Alle Kapitel hier tragen `audience: 'admin'`. Sie bleiben trotzdem für
 * jeden lesbar -- wer wissen will, warum ein Status die SLA-Uhr anhält, soll
 * das nachlesen können, auch wenn er den Haken nicht selbst setzen darf.
 */

const stammdaten: HandbookChapter = {
  id: 'stammdaten',
  title: 'Stammdaten',
  summary: 'Warteschlangen, Status, Typen, Prioritäten, Tätigkeitsarten, Tags.',
  audience: 'admin',
  route: '/app/administration/master-data',
  sections: [
    {
      id: 'grundsatz',
      title: 'Der Grundsatz',
      blocks: [
        {
          kind: 'shot',
          file: 'stammdaten',
          caption: 'Die Stammdaten, eine Art je Reiter.',
        },
        {
          kind: 'text',
          text: 'Stammdaten sind die Bausteine, mit denen ein Mandant sein Ticketwesen zuschneidet. Drei Angaben kehren überall wieder: „Vorgabe“ (wird verwendet, wenn nichts gewählt wird — es kann nur eine geben), „Aktiv“ (abgeschaltete Einträge erscheinen in neuen Formularen nicht mehr, bleiben an bestehenden Tickets aber erhalten) und „Reihenfolge“.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Abschalten statt löschen',
          text: 'Wer eine Priorität löscht, die an dreitausend Tickets hängt, verliert die Auswertung dieser Tickets. Abschalten hält die Vergangenheit lesbar und die Zukunft sauber. Löschen ist für Einträge, die nie benutzt wurden.',
        },
      ],
    },
    {
      id: 'warteschlangen',
      title: 'Warteschlangen',
      blocks: [
        {
          kind: 'text',
          text: 'Eine Warteschlange ist die fachliche Zuständigkeit. Sie trägt mehr als einen Namen:',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'E-Mail-Adresse',
              text: 'Unter dieser Adresse gehen Tickets für diese Warteschlange ein. Wirksam wird sie mit dem E-Mail-Kanal.',
            },
            {
              term: 'Standardbearbeiter',
              text: 'Wer neue Tickets bekommt, wenn niemand anderes gewählt wird.',
            },
            {
              term: 'Signatur',
              text: 'Unter Mails aus dieser Warteschlange. Ohne Auswahl gilt die Vorgabesignatur des Mandanten — der Service Desk und die Buchhaltung unterschreiben verschieden.',
            },
            {
              term: 'Geschäftszeiten',
              text: 'Ohne Auswahl gilt der Kalender der Servicezusage. Eine Bereitschaft rechnet anders als der Tagesdienst.',
            },
          ],
        },
      ],
    },
    {
      id: 'status',
      title: 'Ticketstatus',
      blocks: [
        {
          kind: 'text',
          text: 'Der Name ist frei, die Statusgruppe nicht. Die Gruppe bestimmt Auswertung, SLA-Uhr und Sichtbarkeit im Portal — sechs feste Werte, siehe das Kapitel „Das Ticket verstehen“.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: '„Hält die SLA-Uhr an“',
          text: 'Dieser Haken entscheidet, ob die Frist während des Wartens weiterläuft. Er gehört an Zustände, in denen Sie nicht am Zug sind — „Wartet auf Kundenrückmeldung“ ja, „Wartet auf Freigabe des Chefs“ eher nicht: Das ist Ihr Haus.',
        },
        { kind: 'route', to: '/app/administration/master-data', label: 'Zu den Stammdaten' },
      ],
    },
    {
      id: 'weitere',
      title: 'Typen, Prioritäten, Auswirkung, Dringlichkeit, Tätigkeitsarten, Tags',
      blocks: [
        {
          kind: 'terms',
          items: [
            {
              term: 'Tickettypen',
              text: 'Der ITIL-Typ ist fest: Störung, Serviceanfrage, Problem, Änderung. Der angezeigte Name ist frei — ein Mandant darf „Serviceanfrage“ auch „Bestellung“ nennen.',
            },
            {
              term: 'Prioritäten',
              text: 'Mit Stufe und Farbe. Stufe 1 ist die höchste. An der Priorität hängen die SLA-Ziele.',
            },
            {
              term: 'Auswirkung und Dringlichkeit',
              text: 'Die beiden Achsen der Prioritätsmatrix, ebenfalls mit Stufen.',
            },
            {
              term: 'Tätigkeitsarten',
              text: 'Wofür Zeit erfasst wird. Je Art lässt sich vorbelegen, ob sie in der Regel abrechenbar ist.',
            },
            {
              term: 'Tags',
              text: 'Freie Etiketten mit Farbe, quer zu allem anderen. Für das, was sich nicht in eine Kategorie pressen lässt.',
            },
          ],
        },
      ],
    },
  ],
};

const kategorien: HandbookChapter = {
  id: 'kategorien',
  title: 'Kategorien',
  summary: 'Der mehrstufige Kategoriebaum für Routing und Auswertung.',
  audience: 'admin',
  route: '/app/administration/categories',
  sections: [
    {
      id: 'baum',
      title: 'Der Kategoriebaum',
      blocks: [
        { kind: 'shot', file: 'kategorien', caption: 'Der Kategoriebaum mit drei Ebenen.' },
        {
          kind: 'text',
          text: 'Kategorien ordnen Tickets fachlich ein. Höchstens drei Ebenen — eine vierte wäre in der Oberfläche nicht mehr erreichbar und damit unsichtbar vorhanden. Die Grenze setzt zusätzlich die Datenbank.',
        },
        {
          kind: 'example',
          title: 'Beispiel',
          text: 'Hardware → Drucker → Papierstau. Software → Warenwirtschaft → Buchungslauf. Netzwerk → WLAN. Zwei Ebenen genügen meist; die dritte ist für den Fall, dass ein Zweig wirklich zerfällt.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Erst denken, dann bauen',
          text: 'Ein Kategoriebaum, der sich alle drei Monate ändert, macht jede Auswertung wertlos, weil die Vergangenheit nach dem alten Schnitt eingeordnet ist. Bauen Sie ihn flach und ändern Sie ihn selten.',
        },
        { kind: 'route', to: '/app/administration/categories', label: 'Zu den Kategorien' },
      ],
    },
  ],
};

const matrix: HandbookChapter = {
  id: 'prioritaetsmatrix',
  title: 'Prioritätsmatrix',
  summary: 'Aus Auswirkung und Dringlichkeit wird die Priorität.',
  audience: 'admin',
  route: '/app/administration/priority-matrix',
  sections: [
    {
      id: 'matrix',
      title: 'Die Matrix',
      blocks: [
        {
          kind: 'shot',
          file: 'prioritaetsmatrix',
          caption: 'Auswirkung in den Zeilen, Dringlichkeit in den Spalten.',
        },
        {
          kind: 'steps',
          steps: [
            {
              text: 'Zuerst Auswirkungs- und Dringlichkeitsstufen in den Stammdaten anlegen.',
              detail:
                'Ohne Achsen gibt es keine Matrix. Zwei bis vier Stufen je Achse sind üblich.',
            },
            { text: 'In jede Zelle die Priorität eintragen, die sich daraus ergeben soll.' },
            {
              text: '„Matrix speichern“.',
              detail:
                'Jede Zelle muss gefüllt sein. Eine Lücke wäre ein Ticket, dessen Priorität sich nicht ermitteln lässt.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Die Matrix lässt sich abschalten',
          text: 'Ist sie aus, wird die Priorität beim Anlegen unmittelbar gewählt. Für kleine Mandanten ist das der einfachere Weg. Der Preis: Zwei Bearbeiter beurteilen denselben Fall verschieden.',
        },
      ],
    },
  ],
};

const sla: HandbookChapter = {
  id: 'sla',
  title: 'Servicezusagen und Geschäftszeiten',
  summary: 'Welche Fristen zugesagt sind, wann sie laufen und wann sie anhalten.',
  audience: 'admin',
  route: '/app/administration/sla',
  sections: [
    {
      id: 'kalender',
      title: 'Zuerst der Geschäftszeitenkalender',
      blocks: [
        {
          kind: 'shot',
          file: 'sla',
          caption: 'Zusagen links, Geschäftszeiten rechts.',
        },
        {
          kind: 'text',
          text: 'Eine Frist wird nie in Kalendertagen gerechnet, sondern ausschließlich in Geschäftszeiten. Deshalb kommt der Kalender zuerst.',
        },
        {
          kind: 'steps',
          steps: [
            { text: '„Neuer Geschäftszeitenkalender“ klicken, Namen und Zeitzone setzen.' },
            {
              text: 'Je Wochentag ein Zeitfenster eintragen — etwa Montag bis Freitag, 8:00 bis 17:00.',
              detail:
                'Mehrere Fenster je Tag sind möglich. Eine Mittagspause, in der die Uhr steht, ist zwei Fenster.',
            },
            {
              text: 'Feiertage eintragen.',
              detail:
                'Ein Feiertag fällt ganz aus. Ein halber Feiertag ist ein zweites Zeitfenster. „Jährlich wiederkehrend“ für feste Daten wie den 3. Oktober; bewegliche Feiertage werden je Jahr eingetragen.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Die Zeitzone gilt für die Rechnung',
          text: 'Zeitumstellung im Frühjahr und Herbst rechnet das System richtig. Wer die Zeitzone falsch setzt, verschiebt jede Frist um eine Stunde — und merkt es genau dann, wenn sie reißt.',
        },
      ],
    },
    {
      id: 'zusage',
      title: 'Dann die Servicezusage',
      blocks: [
        {
          kind: 'steps',
          steps: [
            { text: '„Neue Servicezusage“ klicken und einen Kalender zuordnen.' },
            {
              text: 'Warnschwelle setzen — in Prozent der verbrauchten Frist.',
              detail:
                'Bei 80 wird gewarnt, wenn noch ein Fünftel übrig ist. Zu spät gewarnt ist so gut wie nicht gewarnt.',
            },
            {
              text: '„Ziele bearbeiten“: je Priorität die Reaktions- und die Lösungszeit in Minuten.',
              detail:
                'Leer heißt: keine Zusage für diese Priorität. Bestehende Tickets behalten ihre Frist — eine Änderung wirkt nach vorn.',
            },
          ],
        },
        {
          kind: 'example',
          title: 'Beispiel',
          text: 'Priorität „Kritisch“: Reaktion 30 Minuten, Lösung 240 Minuten. Kalender Mo–Fr 8–17 Uhr. Ein Ticket, das Freitag um 16:30 eingeht, hat die Reaktionsfrist um 17:00 halb verbraucht — der Rest läuft Montag ab 8:00 weiter. Die Lösungsfrist von vier Geschäftsstunden endet Montag um 11:30.',
        },
      ],
    },
    {
      id: 'uhr',
      title: 'Die SLA-Uhr im Betrieb',
      blocks: [
        {
          kind: 'terms',
          items: [
            {
              term: 'Reaktionszeit',
              text: 'Bis zur ersten Antwort an den Kunden. Eine interne Notiz beendet sie nicht.',
            },
            { term: 'Lösungszeit', text: 'Bis das Ticket in einer abschließenden Gruppe steht.' },
            {
              term: 'Angehalten',
              text: 'Das Ticket steht in einem Status mit dem Haken „Hält die SLA-Uhr an“. Die Frist läuft nicht.',
            },
            {
              term: 'Warnung',
              text: 'Die Warnschwelle ist erreicht. Der Balken am Ticket zeigt es, und wer benachrichtigt werden will, stellt das in seinen Einstellungen ein.',
            },
            { term: 'Überschritten', text: 'Die Frist ist gerissen. Steht auch in den Berichten.' },
          ],
        },
        {
          kind: 'text',
          text: '„SLA-Uhr jetzt prüfen“ stößt den Wächter von Hand an und meldet, wie viele Uhren geprüft wurden, wie viele Warnungen und wie viele überschrittene Fristen es gibt. Im Betrieb erledigt das der Hintergrunddienst.',
        },
        { kind: 'route', to: '/app/administration/sla', label: 'Zu den Servicezusagen' },
      ],
    },
  ],
};

const felder: HandbookChapter = {
  id: 'eigene-felder',
  title: 'Eigene Felder',
  summary: 'Zusätzliche Angaben an Tickets, Kunden und Ansprechpartnern.',
  audience: 'admin',
  route: '/app/administration/custom-fields',
  sections: [
    {
      id: 'anlegen',
      title: 'Ein Feld festlegen',
      blocks: [
        { kind: 'shot', file: 'eigene-felder', caption: 'Die festgelegten eigenen Felder.' },
        {
          kind: 'steps',
          steps: [
            {
              text: 'Bei „Gilt für“ wählen, woran das Feld hängt: Ticket, Kunde oder Ansprechpartner.',
            },
            {
              text: 'Technischen Namen vergeben.',
              detail:
                'Er erscheint in der Schnittstelle und im Export und ist nachträglich nicht änderbar. Wählen Sie ihn technisch, nicht sprachlich: seriennummer, nicht Seriennummer_neu2.',
            },
            { text: 'Beschriftung und Hilfetext setzen — das sieht der Bearbeiter.' },
            {
              text: 'Feldtyp wählen.',
              detail:
                'Text, Mehrzeiliger Text, Ganze Zahl, Dezimalzahl, Ja/Nein, Datum, Zeitpunkt, Auswahl, Mehrfachauswahl, Adresse, E-Mail-Adresse. Nachträglich nicht änderbar — das würde bereits erfasste Werte umdeuten.',
            },
            { text: 'Bei Auswahl und Mehrfachauswahl: eine Option je Zeile.' },
            {
              text: 'Pflichtfeld ankreuzen, wenn ohne diese Angabe nicht gearbeitet werden kann.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Jedes Feld ist eine Datenerhebung',
          text: 'Ein eigenes Feld an einem Ansprechpartner erhebt personenbezogene Daten. Es gehört ins Verzeichnis der Verarbeitungstätigkeiten und in das Feldverzeichnis unter „Datenschutz“. Legen Sie nur an, was Sie brauchen — und nicht, was vielleicht einmal nützlich sein könnte.',
        },
      ],
    },
  ],
};

const bausteine: HandbookChapter = {
  id: 'bausteine',
  title: 'Textbausteine, Ticketvorlagen, Makros',
  summary: 'Die drei Abkürzungen für wiederkehrende Arbeit — und wo der Unterschied liegt.',
  audience: 'admin',
  sections: [
    {
      id: 'unterschied',
      title: 'Was wovon abzugrenzen ist',
      blocks: [
        {
          kind: 'terms',
          items: [
            {
              term: 'Textbaustein',
              text: 'Eine Formulierung. Wird beim Schreiben eines Beitrags eingefügt und ändert sonst nichts.',
            },
            {
              term: 'Ticketvorlage',
              text: 'Vorbelegte Felder beim Anlegen. Was Sie eintragen, gewinnt gegen die Vorlage.',
            },
            {
              term: 'Makro',
              text: 'Eine Folge von Aktionen an einem bestehenden Ticket. Ändert Felder, setzt Tags, schreibt Beiträge.',
            },
          ],
        },
      ],
    },
    {
      id: 'textbausteine',
      title: 'Textbausteine',
      blocks: [
        {
          kind: 'shot',
          file: 'textbausteine',
          caption: 'Textbausteine mit Kürzel und Platzhaltern.',
        },
        {
          kind: 'text',
          text: 'Ein Baustein hat ein Kürzel — Kleinbuchstaben, Ziffern, Punkt, Bindestrich, Unterstrich — und einen Text mit Platzhaltern. Geschrieben wird er im selben Editor wie ein Beitrag, samt Auszeichnung; sie kommt mit, wenn der Baustein eingefügt wird. Beim Einfügen werden die Platzhalter durch die Werte des Tickets ersetzt. Bleibt ein Platzhalter ohne Wert, meldet die Oberfläche das und bittet um Ergänzung von Hand, statt eine Lücke stehen zu lassen.',
        },
        { kind: 'route', to: '/app/administration/text-snippets', label: 'Zu den Textbausteinen' },
      ],
    },
    {
      id: 'vorlagen',
      title: 'Ticketvorlagen',
      blocks: [
        { kind: 'shot', file: 'ticketvorlagen', caption: 'Ticketvorlagen mit ihrer Vorbelegung.' },
        {
          kind: 'text',
          text: 'Eine Vorlage belegt Warteschlange, Typ, Priorität, Kategorie, Betreff und Text vor. Der übliche Anlass sind wiederkehrende Vorgänge: der Einrichtungsauftrag für einen neuen Mitarbeiter, die vierteljährliche Wartung, der Austritt.',
        },
        {
          kind: 'route',
          to: '/app/administration/ticket-templates',
          label: 'Zu den Ticketvorlagen',
        },
      ],
    },
    {
      id: 'makros',
      title: 'Makros',
      blocks: [
        { kind: 'shot', file: 'makros', caption: 'Ein Makro und seine Aktionsfolge.' },
        {
          kind: 'text',
          text: 'Ein Makro ist eine feste Liste von Aktionen und kein Programm. Das ist Absicht: Wäre es ein Programm, wäre jede Rechteprüfung im System eine Frage seiner Auslegung.',
        },
        {
          kind: 'table',
          head: ['Aktion', 'Wirkung'],
          rows: [
            ['Warteschlange setzen', 'Verschiebt das Ticket in eine andere Zuständigkeit'],
            ['Priorität setzen', 'Auch die Frist richtet sich danach'],
            ['Typ setzen', 'Störung, Serviceanfrage, Problem, Änderung'],
            ['Kategorie setzen', 'Fachliche Einordnung'],
            ['Status setzen', 'Zustandswechsel'],
            ['Bearbeiter setzen', 'Namentlich'],
            ['Verantwortlichen setzen', 'Namentlich'],
            ['Tag hinzufügen / entfernen', 'Etiketten'],
            ['Interne Notiz schreiben', 'Bleibt intern'],
            ['Antwort schreiben', 'Geht an den Kunden hinaus'],
            ['Zuweisen: reihum', 'Der nächste Bearbeiter in der Runde'],
            ['Zuweisen: nach Auslastung', 'Wer die wenigsten offenen Tickets hat'],
          ],
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Ein Makro mit „Antwort schreiben“ verschickt eine Mail',
          text: 'Dieselbe Aktionsliste trägt auch die Regel-Engine. Ein Makro wendet ein Mensch an; eine Regel läuft von selbst. Bei beiden gilt: Was hinausgeht, geht hinaus.',
        },
        { kind: 'route', to: '/app/administration/macros', label: 'Zu den Makros' },
      ],
    },
    {
      id: 'checklisten',
      title: 'Checklistenvorlagen',
      blocks: [
        {
          kind: 'shot',
          file: 'checklistenvorlagen',
          caption: 'Eine Checklistenvorlage mit ihren Punkten.',
        },
        {
          kind: 'text',
          text: 'Eine Zeile je Punkt. Eine Vorlage aus einem Absatz lässt sich nicht abhaken. Beim Einfügen am Ticket werden die Punkte kopiert — eine spätere Änderung an der Vorlage schreibt keine laufende Abarbeitung um, sonst verschwände ein bereits gesetzter Haken.',
        },
        {
          kind: 'route',
          to: '/app/administration/checklist-templates',
          label: 'Zu den Checklistenvorlagen',
        },
      ],
    },
  ],
};

export const verwaltungChapters: HandbookChapter[] = [
  stammdaten,
  kategorien,
  matrix,
  sla,
  felder,
  bausteine,
];
